import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { PRODUCTS } from "@/data/products";
import { ArrowUpRight } from "lucide-react";

const CATEGORY_KEYS = ["all", "empanadas", "dulces", "salados", "bebidas"];

const Productos = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState("all");

  const filtered = useMemo(() => {
    if (active === "all") return PRODUCTS;
    return PRODUCTS.filter((p) => p.category === active);
  }, [active]);

  return (
    <div data-testid="page-productos">
      {/* Header */}
      <section className="px-6 md:px-12 lg:px-20 pt-16 md:pt-24 pb-12">
        <div className="max-w-[1500px] mx-auto">
          <div className="text-[11px] uppercase tracking-[0.3em] text-caramel mb-6 font-medium">
            {t.products.overline}
          </div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-ink">
            {t.products.title}
          </h1>
          <p className="mt-8 text-lg md:text-xl text-ink-muted font-light max-w-2xl leading-relaxed">
            {t.products.sub}
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <section className="sticky top-20 md:top-24 z-30 bg-bg/95 backdrop-blur-md border-y border-bone">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-5 overflow-x-auto">
          <div className="flex gap-8 md:gap-10 whitespace-nowrap">
            {CATEGORY_KEYS.map((cat, idx) => (
              <button
                key={cat}
                data-testid={`filter-${cat}`}
                onClick={() => setActive(cat)}
                className={`text-[11px] uppercase tracking-[0.25em] font-medium transition-colors pb-1 border-b ${
                  active === cat
                    ? "text-wine border-wine"
                    : "text-ink-muted border-transparent hover:text-ink"
                }`}
              >
                {t.products.filters[idx]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {filtered.map((p, idx) => (
              <article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                className="group"
              >
                <Link to="/tienda" className="block">
                  <div className="relative aspect-[4/5] overflow-hidden bg-surface">
                    <img
                      src={p.image}
                      alt={p.name[lang]}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-caramel font-medium mb-2">
                        {p.category}
                      </div>
                      <h3 className="font-serif text-2xl text-ink font-light tracking-tight leading-tight">
                        {p.name[lang] || p.name.es}
                      </h3>
                      <p className="text-sm text-ink-muted font-light mt-3 line-clamp-2">
                        {p.description[lang] || p.description.es}
                      </p>
                    </div>
                    <ArrowUpRight
                      className="w-5 h-5 text-wine flex-shrink-0 mt-2 transition-transform group-hover:rotate-45"
                      strokeWidth={1.5}
                    />
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Productos;
